import IntranetLayout from "@/components/IntranetLayout";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { formatReadingTime } from "@/hooks/useDocumentReadingTime";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Timer, Users, FileText, Search, Clock, TrendingUp, BarChart3 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

const EXECUTIVE_IDS = [
  "bb385f23-ed54-4a65-ba02-8b80b567bf62", // Bo Jardine
  "af9054fe-4735-4981-85d8-1de34c32da12", // Jerome Arnold
  "528b9027-60d4-41f9-bffc-283fa61e4efb", // Kyle Pemberton
];

const EmployeeReadingTimeReport = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchUser, setSearchUser] = useState("");
  const [searchDoc, setSearchDoc] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [groupBy, setGroupBy] = useState<"user" | "document">("user");

  const isExecutive = user && EXECUTIVE_IDS.includes(user.id);

  useEffect(() => {
    if (!isExecutive) return;

    const handleReadingTimeUpdate = () => {
      void queryClient.invalidateQueries({ queryKey: ["exec-reading-times"] });
    };

    window.addEventListener("reading-time-updated", handleReadingTimeUpdate);
    return () => window.removeEventListener("reading-time-updated", handleReadingTimeUpdate);
  }, [isExecutive, queryClient]);

  // Load all reading time records (executives can see all via admin-like query)
  const { data: allReadingTimes = [], isLoading } = useQuery({
    queryKey: ["exec-reading-times"],
    queryFn: async () => {
      const { data } = await supabase
        .from("document_reading_time" as any)
        .select("*")
        .order("updated_at", { ascending: false });
      return (data as any) ?? [];
    },
    enabled: !!isExecutive,
    refetchInterval: isExecutive ? 5000 : false,
    refetchOnWindowFocus: true,
  });

  // Load all profiles
  const { data: profiles = [] } = useQuery({
    queryKey: ["exec-profiles"],
    queryFn: async () => {
      const { data } = await supabase.from("profiles").select("user_id, full_name");
      return data ?? [];
    },
    enabled: !!isExecutive,
  });

  const formatLastOpened = (timestamp: string) =>
    new Date(timestamp).toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const getProfileName = (userId: string) => {
    const p = profiles.find((pr: any) => pr.user_id === userId);
    return p?.full_name || "Unknown";
  };

  const filtered = useMemo(() => {
    return allReadingTimes.filter((r: any) => {
      const userName = getProfileName(r.user_id).toLowerCase();
      const docName = (r.document_name || "").toLowerCase();
      const matchUser = !searchUser || userName.includes(searchUser.toLowerCase());
      const matchDoc = !searchDoc || docName.includes(searchDoc.toLowerCase());
      const matchSource = sourceFilter === "all" || r.source === sourceFilter;
      return matchUser && matchDoc && matchSource;
    });
  }, [allReadingTimes, searchUser, searchDoc, sourceFilter, profiles]);

  // Stats
  const totalUsers = useMemo(() => new Set(filtered.map((r: any) => r.user_id)).size, [filtered]);
  const totalDocs = useMemo(() => new Set(filtered.map((r: any) => r.document_id)).size, [filtered]);
  const totalSeconds = useMemo(() => filtered.reduce((s: number, r: any) => s + (r.total_seconds || 0), 0), [filtered]);
  const avgPerUser = totalUsers > 0 ? Math.round(totalSeconds / totalUsers) : 0;

  // Grouped views
  const groupedByUser = useMemo(() => {
    const map: Record<string, { name: string; docs: any[]; totalSeconds: number }> = {};
    filtered.forEach((r: any) => {
      if (!map[r.user_id]) map[r.user_id] = { name: getProfileName(r.user_id), docs: [], totalSeconds: 0 };
      map[r.user_id].docs.push(r);
      map[r.user_id].totalSeconds += r.total_seconds || 0;
    });
    return Object.entries(map).sort((a, b) => b[1].totalSeconds - a[1].totalSeconds);
  }, [filtered, profiles]);

  const groupedByDoc = useMemo(() => {
    const map: Record<string, { name: string; source: string; users: any[]; totalSeconds: number }> = {};
    filtered.forEach((r: any) => {
      if (!map[r.document_id]) map[r.document_id] = { name: r.document_name, source: r.source, users: [], totalSeconds: 0 };
      map[r.document_id].users.push(r);
      map[r.document_id].totalSeconds += r.total_seconds || 0;
    });
    return Object.entries(map).sort((a, b) => b[1].totalSeconds - a[1].totalSeconds);
  }, [filtered]);

  if (!isExecutive) {
    return (
      <IntranetLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Access restricted to executive team members.</p>
        </div>
      </IntranetLayout>
    );
  }

  const sourceBadge = (source: string) => {
    const colors: Record<string, string> = {
      documents: "bg-blue-50 text-blue-700 border-blue-200",
      onboarding: "bg-emerald-50 text-emerald-700 border-emerald-200",
      sms_review: "bg-amber-50 text-amber-700 border-amber-200",
      safety_bulletin: "bg-rose-50 text-rose-700 border-rose-200",
    };
    return <Badge variant="outline" className={`text-[10px] ${colors[source] || ""}`}>{source}</Badge>;
  };

  return (
    <IntranetLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-display font-bold text-foreground">Document Reading Analytics</h1>
          <p className="text-sm text-muted-foreground font-body mt-1">
            Monitor employee engagement with company documents, SOPs, onboarding materials, and safety bulletins.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Users className="w-3.5 h-3.5" /> Employees Tracked</div>
              <p className="text-2xl font-display font-bold">{totalUsers}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><FileText className="w-3.5 h-3.5" /> Documents Opened</div>
              <p className="text-2xl font-display font-bold">{totalDocs}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Timer className="w-3.5 h-3.5" /> Total Reading Time</div>
              <p className="text-2xl font-display font-bold">{formatReadingTime(totalSeconds)}</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><TrendingUp className="w-3.5 h-3.5" /> Avg per Employee</div>
              <p className="text-2xl font-display font-bold">{formatReadingTime(avgPerUser)}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder="Search employee..." value={searchUser} onChange={(e) => setSearchUser(e.target.value)} className="pl-8 h-9 text-sm" />
              </div>
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder="Search document..." value={searchDoc} onChange={(e) => setSearchDoc(e.target.value)} className="pl-8 h-9 text-sm" />
              </div>
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-[160px] h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="documents">Documents & SOPs</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="sms_review">SMS Review</SelectItem>
                  <SelectItem value="safety_bulletin">Safety Bulletins</SelectItem>
                </SelectContent>
              </Select>
              <Select value={groupBy} onValueChange={(v) => setGroupBy(v as any)}>
                <SelectTrigger className="w-[160px] h-9 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Group by Employee</SelectItem>
                  <SelectItem value="document">Group by Document</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Data */}
        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading reading data...</div>
        ) : groupBy === "user" ? (
          <div className="space-y-3">
            {groupedByUser.map(([userId, data]) => (
              <Card key={userId}>
                <CardHeader className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-display flex items-center gap-2">
                      <Users className="w-4 h-4 text-teal" />
                      {data.name}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-[10px]">{data.docs.length} documents</Badge>
                      <Badge className="bg-teal/10 text-teal border-teal/20 text-[10px]">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatReadingTime(data.totalSeconds)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-3 pt-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px] h-8">Document</TableHead>
                        <TableHead className="text-[10px] h-8">Source</TableHead>
                        <TableHead className="text-[10px] h-8">Time Spent</TableHead>
                        <TableHead className="text-[10px] h-8">Last Opened</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.docs.sort((a: any, b: any) => b.total_seconds - a.total_seconds).map((r: any) => (
                        <TableRow key={r.id}>
                          <TableCell className="text-xs py-1.5 max-w-[300px] truncate">{r.document_name}</TableCell>
                          <TableCell className="py-1.5">{sourceBadge(r.source)}</TableCell>
                          <TableCell className="text-xs py-1.5 font-mono">{formatReadingTime(r.total_seconds)}</TableCell>
                          <TableCell className="text-xs py-1.5 text-muted-foreground">
                            {formatLastOpened(r.last_opened_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
            {groupedByUser.length === 0 && (
              <div className="text-center py-12 text-muted-foreground text-sm">No reading time data found.</div>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {groupedByDoc.map(([docId, data]) => (
              <Card key={docId}>
                <CardHeader className="py-3 px-4">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-display flex items-center gap-2 max-w-[60%] truncate">
                      <FileText className="w-4 h-4 text-teal flex-shrink-0" />
                      {data.name}
                    </CardTitle>
                    <div className="flex items-center gap-2">
                      {sourceBadge(data.source)}
                      <Badge variant="outline" className="text-[10px]">{data.users.length} employees</Badge>
                      <Badge className="bg-teal/10 text-teal border-teal/20 text-[10px]">
                        <Clock className="w-3 h-3 mr-1" />
                        {formatReadingTime(data.totalSeconds)}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="px-4 pb-3 pt-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-[10px] h-8">Employee</TableHead>
                        <TableHead className="text-[10px] h-8">Time Spent</TableHead>
                        <TableHead className="text-[10px] h-8">Last Opened</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data.users.sort((a: any, b: any) => b.total_seconds - a.total_seconds).map((r: any) => (
                        <TableRow key={r.id}>
                          <TableCell className="text-xs py-1.5">{getProfileName(r.user_id)}</TableCell>
                          <TableCell className="text-xs py-1.5 font-mono">{formatReadingTime(r.total_seconds)}</TableCell>
                          <TableCell className="text-xs py-1.5 text-muted-foreground">
                            {formatLastOpened(r.last_opened_at)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            ))}
            {groupedByDoc.length === 0 && (
              <div className="text-center py-12 text-muted-foreground text-sm">No reading time data found.</div>
            )}
          </div>
        )}
      </div>
    </IntranetLayout>
  );
};

export default EmployeeReadingTimeReport;
